/**
 * De planning van één project in de uitklap van "Nog in te plannen".
 *
 * Eerste versie hergebruikte het dagenraster van Planner To-Do (vier weken).
 * Dat paste niet (Joris, 9 sep): taken van maanden terug stapelden op de
 * eerste kolom, taken in oktober werden als "geen datum" getekend, en niets
 * zei wat "deels gepland" nu betekende. Daarom:
 *
 *  1. één compacte tabel: per discipline geoffreerd · gepland · te plannen
 *     (de opbouw van "gepland" in de tooltip);
 *  2. een weektijdlijn die zich om het project vouwt: per week één staafje uren
 *     per discipline (details bij hover), deadlines als dunne lijn (Joris, 25 sep:
 *     rust — geen chip per taak meer);
 *  3. taken zonder datum als teller, niet als dertig balkjes;
 *  4. daaronder de taken gegroepeerd: zonder eigenaar, zonder datum, komend,
 *     verstreken (dat laatste dichtgeklapt — meestal gedaan, niet afgevinkt);
 *  5. per taak een vinkje om hem hier meteen af te ronden (Joris, 9 sep) —
 *     zelfde PATCH als het Weekbord, acht seconden terug te draaien.
 */
import React, { useMemo, useState } from 'react';
import { Box, Checkbox, Collapse, Stack, Tooltip, Typography } from '@mui/material';
import { toast } from 'react-hot-toast';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { biedOngedaanAan } from '../../utils/ongedaan';
import { ExpandMore as ExpandMoreIcon, ChevronRight as ChevronRightIcon } from '@mui/icons-material';
import { datumInVenster, datumKort, samenvatDiscipline, urenPerWeek, uurHeel, weekVenster } from '../../utils/inplannen';

const FONT = '"Inter", system-ui, sans-serif';
const LABEL_BREEDTE = 150;

export const DISCIPLINES = [
  { key: 'montage', label: 'Montage' },
  { key: 'animatie', label: 'Animatie' },
  { key: 'feedback', label: 'Feedback' },
];

const TOESTAND = {
  zonder_eigenaar: { tekst: 'Zonder eigenaar' },
  toegewezen: { tekst: 'Zonder datum' },
  komend: { tekst: 'Gepland' },
  verstreken: { tekst: 'Verstreken' },
};

const TEKST = '#1f2937';
const GEDEMPT = '#6b7280';
const LICHT = '#9ca3af';
const LIJN = '#eef0f3';
const ACCENT = '#526075'; // primary; "te plannen" is het enige accent in de uitklap

const groepVan = (t) => (t.toestand === 'gepland' ? (t.verstreken ? 'verstreken' : 'komend') : t.toestand);
const leegBijNul = (n) => (Math.round(Number(n) || 0) ? `${uurHeel(n)} u` : '');

/**
 * Eén compacte tabel: rijen = disciplines, kolommen Geoffreerd · Gepland · Te plannen.
 * Nulwaarden blijven leeg; "te plannen" is het enige accent. De opbouw van "gepland"
 * (komend, al voorbij, zonder datum, zonder eigenaar) staat in de tooltip.
 */
function Samenvatting({ rij, disciplines }) {
  const rijen = DISCIPLINES.filter((d) => disciplines.includes(d.key))
    .map((d) => ({ d, s: samenvatDiscipline(rij.disciplines?.[d.key]) }))
    .filter(({ s }) => s.offerte || s.geteld || s.zonderEigenaar || s.zonderDatum);
  if (!rijen.length) return <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: GEDEMPT }}>Niets geoffreerd, geen taken.</Typography>;
  return (
    <Box component="table" sx={{ borderCollapse: 'collapse', fontFamily: FONT, fontSize: 12.5, fontVariantNumeric: 'tabular-nums',
      '& th': { fontWeight: 500, color: GEDEMPT, fontSize: 11.5, textAlign: 'right', px: 1.25, py: 0.4, whiteSpace: 'nowrap' },
      '& th:first-of-type': { textAlign: 'left', pl: 0 },
      '& td': { textAlign: 'right', px: 1.25, py: 0.45, borderTop: `1px solid ${LIJN}`, whiteSpace: 'nowrap', color: TEKST },
      '& td:first-of-type': { textAlign: 'left', pl: 0 } }}>
      <thead><tr><th aria-label="discipline" /><th>Geoffreerd</th><th>Gepland</th><th>Te plannen</th></tr></thead>
      <tbody>
        {rijen.map(({ d, s }) => {
          const opbouw = [
            s.toekomst && `${uurHeel(s.toekomst)} u komend`,
            s.verstreken && `${uurHeel(s.verstreken)} u al voorbij (niet afgevinkt)`,
            s.zonderDatum && `${uurHeel(s.zonderDatum)} u met eigenaar, zonder datum (telt half)`,
            s.zonderEigenaar && `${uurHeel(s.zonderEigenaar)} u zonder eigenaar (telt niet)`,
          ].filter(Boolean);
          return (
            <tr key={d.key}>
              <td>{d.label}</td>
              <td>{leegBijNul(s.offerte)}</td>
              <td>
                {opbouw.length ? (
                  <Tooltip arrow title={<Box>{opbouw.map((r) => <div key={r}>{r}</div>)}</Box>}>
                    <Box component="span" sx={{ cursor: 'help', borderBottom: `1px dotted ${LICHT}` }}>{leegBijNul(s.geteld) || '0 u'}</Box>
                  </Tooltip>
                ) : leegBijNul(s.geteld)}
              </td>
              <td style={{ color: ACCENT, fontWeight: 700 }}>{s.offerte ? leegBijNul(s.open) : ''}</td>
            </tr>
          );
        })}
      </tbody>
    </Box>
  );
}

const STAAF_MAX = 26;

/**
 * Weektijdlijn: per discipline per week één staafje (hoogte = uren) met het aantal
 * erboven; de taken van die week staan in de tooltip. Deadlines als dunne lijn.
 */
function Tijdlijn({ rij, taken, disciplines }) {
  const venster = useMemo(() => weekVenster({ taken, deadlines: (rij.deliverables || []).map((x) => x.deadline).filter(Boolean) }), [taken, rij.deliverables]);
  const perDiscipline = useMemo(() => urenPerWeek(taken, venster, disciplines), [taken, venster, disciplines]);
  const rijen = DISCIPLINES.filter((d) => disciplines.includes(d.key)).map((d) => ({ d, r: perDiscipline[d.key] }))
    .filter(({ r }) => r && (r.uren.some(Boolean) || r.zonderDatum.aantal || r.eerder || r.later));
  if (!rijen.length) return null;
  const max = Math.max(1, ...rijen.flatMap(({ r }) => r.uren));
  const n = venster.weken.length;
  const deadlines = (rij.deliverables || []).filter((x) => x.deadline)
    .map((x) => ({ ...x, p: datumInVenster(x.deadline, venster) })).filter((x) => x.p);
  const raster = { display: 'grid', gridTemplateColumns: `${LABEL_BREEDTE}px repeat(${n}, minmax(20px, 1fr))` };

  return (
    <Box sx={{ minWidth: 0, flex: 1, overflowX: 'auto' }}>
      <Box sx={{ minWidth: LABEL_BREEDTE + n * 20, position: 'relative' }}>
        <Box sx={{ ...raster, borderBottom: `1px solid ${LIJN}` }}>
          <Box />
          {venster.weken.map((w, i) => (
            <Typography key={w.iso} title={`wk ${w.nummer} · ${w.label}`}
              sx={{ fontFamily: FONT, fontSize: 10, textAlign: 'center', py: 0.25, color: w.huidig ? TEKST : LICHT, fontWeight: w.huidig ? 700 : 400,
                visibility: w.huidig || i % 2 === 0 ? 'visible' : 'hidden' }}>
              {w.nummer}
            </Typography>
          ))}
        </Box>
        {rijen.map(({ d, r }) => {
          const noten = [
            r.zonderDatum.aantal && `${r.zonderDatum.aantal} zonder datum · ${uurHeel(r.zonderDatum.uren)} u`,
            r.eerder && `${uurHeel(r.eerder)} u eerder`,
            r.later && `${uurHeel(r.later)} u later`,
          ].filter(Boolean);
          return (
            <Box key={d.key} sx={{ ...raster, alignItems: 'end', borderBottom: `1px solid ${LIJN}` }}>
              <Box sx={{ py: 0.5, pr: 1, alignSelf: 'center' }}>
                <Typography sx={{ fontFamily: FONT, fontSize: 12, color: TEKST }}>{d.label}</Typography>
                {noten.length > 0 && <Typography sx={{ fontFamily: FONT, fontSize: 10.5, color: GEDEMPT }}>{noten.join(' · ')}</Typography>}
              </Box>
              {r.uren.map((u, i) => {
                const w = venster.weken[i];
                const lijst = r.taken[i];
                const cel = (
                  <Box sx={{ height: STAAF_MAX + 16, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', alignItems: 'center', cursor: u ? 'help' : 'default' }}>
                    {u > 0 && <Typography sx={{ fontFamily: FONT, fontSize: 9.5, color: GEDEMPT, lineHeight: 1.2 }}>{uurHeel(u)}</Typography>}
                    {u > 0 && <Box sx={{ width: '55%', maxWidth: 14, height: Math.max(2, Math.round((u / max) * STAAF_MAX)), bgcolor: w.verleden ? '#d1d5db' : '#6b7280', borderRadius: '2px 2px 0 0' }} />}
                  </Box>
                );
                return u > 0 ? (
                  <Tooltip key={w.iso} arrow placement="top" title={(
                    <Box>
                      <div style={{ fontWeight: 700 }}>{d.label} · wk {w.nummer} · {uurHeel(u)} u</div>
                      {lijst.slice(0, 10).map((t) => (
                        <div key={t.id}>{t.assignees?.length ? `${t.assignees[0].split(' ')[0]} · ` : ''}{t.name} · {uurHeel(Number(t.hours) || 0)} u</div>
                      ))}
                      {lijst.length > 10 && <div>+{lijst.length - 10} meer</div>}
                    </Box>
                  )}>{cel}</Tooltip>
                ) : <Box key={w.iso}>{cel}</Box>;
              })}
            </Box>
          );
        })}
        {/* Deadlines: dunne lijn over de rijen heen */}
        <Box sx={{ position: 'absolute', top: 0, bottom: 0, left: LABEL_BREEDTE, right: 0, pointerEvents: 'none' }}>
          {deadlines.map((x) => (
            <Tooltip key={x.id} arrow title={`Deadline ${x.naam} · ${datumKort(x.deadline)}${x.type ? ` · ${x.type}` : ''}`}>
              <Box sx={{ position: 'absolute', top: 0, bottom: 0, width: 7, ml: '-3px', left: `${((x.p.kolom + x.p.fractie) / n) * 100}%`, pointerEvents: 'auto', cursor: 'help',
                '&::after': { content: '""', position: 'absolute', left: 3, top: 0, bottom: 0, width: '1px', bgcolor: TEKST } }} />
            </Tooltip>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

async function zetPrio(taakId, body) {
  const r = await authFetch(`${BACKEND_URL}/Api/tasks/${taakId}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
}

function Takenlijst({ taken: alleTaken, onGewijzigd }) {
  // Afgevinkte taken meteen uit beeld; de engine telt bij de volgende verversing opnieuw.
  const [verborgen, setVerborgen] = useState(() => new Set());
  const [bezig, setBezig] = useState(null);
  const taken = alleTaken.filter((t) => !verborgen.has(t.id));
  const groepen = ['zonder_eigenaar', 'toegewezen', 'komend', 'verstreken'].map((g) => ({ g, taken: taken.filter((t) => groepVan(t) === g) })).filter((x) => x.taken.length);

  const rondAf = async (t) => {
    setBezig(t.id);
    try {
      await zetPrio(t.id, { PrioNr: 'Afgerond' });
      setVerborgen((v) => new Set([...v, t.id]));
      onGewijzigd?.();
      const vorigePrio = t.priority && t.priority !== 'Afgerond' ? t.priority : 'Geen prio';
      biedOngedaanAan(`${t.name} afgerond`, async () => {
        await zetPrio(t.id, { PrioNr: vorigePrio, Afgerond: false });
        setVerborgen((v) => { const n = new Set(v); n.delete(t.id); return n; });
        onGewijzigd?.();
      });
    } catch (e) {
      toast.error(`Afronden mislukt: ${e.message}`);
    } finally { setBezig(null); }
  };
  const [open, setOpen] = useState(() => Object.fromEntries(['zonder_eigenaar', 'toegewezen', 'komend', 'verstreken'].map((g) => [g, g !== 'verstreken' && alleTaken.filter((t) => groepVan(t) === g).length <= 12])));
  if (!groepen.length) return <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: GEDEMPT, mt: 1 }}>Alle taken afgerond.</Typography>;
  return (
    <Box sx={{ mt: 1.5 }}>
      {groepen.map(({ g, taken: lijst }) => {
        const k = TOESTAND[g];
        const uren = lijst.reduce((s, t) => s + (Number(t.hours) || 0), 0);
        const isOpen = !!open[g];
        return (
          <Box key={g} sx={{ borderTop: `1px solid ${LIJN}` }}>
            <Box onClick={() => setOpen((p) => ({ ...p, [g]: !p[g] }))} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, cursor: 'pointer' }}>
              {isOpen ? <ExpandMoreIcon sx={{ fontSize: 16, color: GEDEMPT }} /> : <ChevronRightIcon sx={{ fontSize: 16, color: LICHT }} />}
              <Typography sx={{ fontFamily: FONT, fontSize: 12, fontWeight: 600, color: TEKST }}>{k.tekst}</Typography>
              <Typography sx={{ fontFamily: FONT, fontSize: 12, color: GEDEMPT }}>{lijst.length} {lijst.length === 1 ? 'taak' : 'taken'} · {uurHeel(uren)} u</Typography>
              {g === 'verstreken' && <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: LICHT }}>— datum voorbij, niet afgevinkt; telt als gepland. Klaar? Vink af.</Typography>}
              {g === 'toegewezen' && <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: LICHT }}>— telt voor de helft mee</Typography>}
            </Box>
            <Collapse in={isOpen}>
              <Box component="table" sx={{ borderCollapse: 'collapse', fontFamily: FONT, fontSize: 12.5, width: '100%', mb: 0.75, '& td': { px: 1, py: 0.3, verticalAlign: 'middle', color: TEKST } }}>
                <tbody>
                  {lijst.map((t) => (
                    <tr key={t.id} style={{ opacity: bezig === t.id ? 0.5 : 1 }}>
                      <td style={{ width: 34, paddingRight: 0 }}>
                        <Tooltip title="Afronden — verdwijnt uit de planning; acht seconden terug te draaien" arrow>
                          <Checkbox size="small" checked={false} disabled={bezig === t.id} onChange={() => rondAf(t)} sx={{ p: 0.25, color: LICHT, '&:hover': { color: TEKST } }} />
                        </Tooltip>
                      </td>
                      <td>{t.name}</td>
                      <td style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums', width: 60 }}>{uurHeel(Number(t.hours) || 0)} u</td>
                      <td style={{ color: t.assignees?.length ? TEKST : LICHT, width: 160 }}>{t.assignees?.length ? t.assignees.join(', ') : 'niemand'}</td>
                      <td style={{ whiteSpace: 'nowrap', color: GEDEMPT, width: 150 }}>
                        {t.startDate ? `${datumKort(String(t.startDate).slice(0, 10))}${t.endDate && t.endDate !== t.startDate ? ` – ${datumKort(String(t.endDate).slice(0, 10))}` : ''}` : <span style={{ color: LICHT }}>geen datum</span>}
                      </td>
                      <td style={{ whiteSpace: 'nowrap', color: GEDEMPT, width: 140 }}>{t.deadline ? `deadline ${datumKort(String(t.deadline).slice(0, 10))}` : ''}</td>
                    </tr>
                  ))}
                </tbody>
              </Box>
            </Collapse>
          </Box>
        );
      })}
    </Box>
  );
}

export default function ProjectPlanning({ rij, disciplines, onGewijzigd }) {
  const taken = (rij.taken || []).filter((t) => disciplines.includes(t.discipline));
  return (
    <Box sx={{ px: 0.5 }}>
      <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3} alignItems={{ xs: 'stretch', lg: 'flex-start' }}>
        <Box sx={{ flexShrink: 0 }}><Samenvatting rij={rij} disciplines={disciplines} /></Box>
        {taken.length > 0 && <Tijdlijn rij={rij} taken={taken} disciplines={disciplines} />}
      </Stack>
      {taken.length ? <Takenlijst taken={taken} onGewijzigd={onGewijzigd} />
        : <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: GEDEMPT, mt: 1 }}>Nog geen montage-, animatie- of feedbacktaken aangemaakt voor dit project.</Typography>}
    </Box>
  );
}
